import React, { useState } from 'react'
import VipLayout from '../../components/vip/VipLayout'
import AlertDialog from '../../components/AlertDialog'
import { useToast } from '../../components/Toast'

type AlertDialogType = 'warning' | 'danger' | 'info' | 'success';

interface FormData {
  name: string
  company: string
  contact: string
  needs: string[]
  message: string
}

const VipContactPage: React.FC = () => {
  const { showToast } = useToast()
  const [formData, setFormData] = useState<FormData>({
    name: '',
    company: '',
    contact: '',
    needs: [],
    message: ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [alertState, setAlertState] = useState<{
    isOpen: boolean
    title: string
    message: string
    type: AlertDialogType
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'info'
  })

  const showAlert = (title: string, message: string, type: AlertDialogType = 'info') => {
    setAlertState({ isOpen: true, title, message, type })
  }

  const hideAlert = () => {
    setAlertState(prev => ({ ...prev, isOpen: false }))
  }

  const needOptions = [
    '企业注册',
    '互联网资质申请',
    'AI 备案',
    '大模型登记或备案',
    '大模型部署或微调/训练',
    '内容服务合规咨询'
  ]

  const handleCheckboxChange = (need: string) => {
    setFormData(prev => ({
      ...prev,
      needs: prev.needs.includes(need)
        ? prev.needs.filter(n => n !== need)
        : [...prev.needs, need]
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      })

      const result = await response.json()

      if (result.success) {
        showToast(result.message || '您的咨询已提交成功！我们会尽快与您联系。', 'success')
        setFormData({
          name: '',
          company: '',
          contact: '',
          needs: [],
          message: ''
        })
      } else {
        showToast(result.message || '提交失败，请稍后重试。', 'error')
      }
    } catch (error) {
      console.error('Submit error:', error)
      showToast('网络错误，请检查网络连接后重试。', 'error')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  return (
    <VipLayout>
      <section className="hero">
        <div className="container">
          <span className="kicker">Contact</span>
          <h1>联系我们</h1>
          <p>如果你正在准备企业注册、互联网资质申请、AI 备案、大模型登记或备案、大模型部署或微调/训练、内容服务合规咨询，欢迎与我们沟通。
            您也可以给我们的咨询邮箱发Email或加咨询微信,留下您的电话，我们会安排顾问回拨沟通。
          </p>
        </div>
      </section>

      <section className="section">
        <div className="container grid cols-2">
          <div className="card contact-card">
            <h3>联系信息</h3>
            <p className="lead" style={{fontSize: '15px'}}>邮箱：fancyai4u@outlook.com</p>
            <p className="lead" style={{fontSize: '15px'}}>微信：Fancyview4U</p>
            <p className="lead" style={{fontSize: '15px'}}>VIP顾问电话咨询：13078096940</p>
          </div>
          <div className="card contact-card">
            <h3>咨询说明</h3>
            <ul className="list">
              <li>请尽量说明企业主营业务与产品形态</li>
              <li>请说明目前已完成或正在申请的资质</li>
              <li>如涉及 AI 服务，请说明模型来源、部署方式与用户对象</li>
              <li>如涉及内容平台，请说明是否有直播、社区、音视频生成等功能</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="section bg-light">
        <div className="container">
          <div className="form-container">
            <h2 className="section-title" style={{textAlign: 'center', marginBottom: '2rem'}}>在线提交咨询</h2>
            <form onSubmit={handleSubmit} className="contact-form">
              <div className="form-group">
                <label htmlFor="name" className="form-label">姓名 <span className="required">*</span></label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="请输入您的姓名"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="company" className="form-label">公司名称 <span className="required">*</span></label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="请输入公司名称"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="contact" className="form-label">联系方式 <span className="required">*</span></label>
                <input
                  type="text"
                  id="contact"
                  name="contact"
                  value={formData.contact}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="请输入手机号或邮箱"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">需求选择（可多选）</label>
                <div className="checkbox-group">
                  {needOptions.map((need) => (
                    <label key={need} className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.needs.includes(need)}
                        onChange={() => handleCheckboxChange(need)}
                        className="checkbox-input"
                      />
                      <span className="checkbox-text">{need}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="message" className="form-label">留言</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  className="form-textarea"
                  placeholder="请详细描述您的需求..."
                  rows={5}
                />
              </div>

              <div className="form-actions">
                <button
                  type="submit"
                  className="btn btn-primary btn-lg"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? '提交中...' : '提交咨询'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      <style>{`
        .form-container {
          max-width: 800px;
          margin: 0 auto;
          padding: 2rem;
          background: rgba(12, 24, 48, 0.72);
          border-radius: 22px;
          box-shadow: 0 20px 60px rgba(25, 89, 170, 0.22);
          border: 1px solid rgba(117, 176, 255, 0.18);
          backdrop-filter: blur(16px);
        }

        .contact-form {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .form-label {
          font-size: 1rem;
          font-weight: 600;
          color: #eaf3ff;
        }

        .required {
          color: #ff6b9d;
        }

        .form-input,
        .form-textarea {
          padding: 0.875rem 1rem;
          font-size: 1rem;
          border: 1px solid rgba(117, 176, 255, 0.25);
          border-radius: 12px;
          transition: border-color 0.2s, box-shadow 0.2s;
          font-family: inherit;
          background: rgba(7, 17, 31, 0.5);
          color: #eaf3ff;
        }

        .form-input::placeholder,
        .form-textarea::placeholder {
          color: #9eb4d4;
          opacity: 0.6;
        }

        .form-input:focus,
        .form-textarea:focus {
          outline: none;
          border-color: #61b3ff;
          box-shadow: 0 0 0 3px rgba(97, 179, 255, 0.15);
          background: rgba(7, 17, 31, 0.7);
        }

        .form-textarea {
          resize: vertical;
          min-height: 120px;
        }

        .checkbox-group {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
          gap: 1rem;
          padding: 1rem;
          background: rgba(7, 17, 31, 0.4);
          border-radius: 12px;
          border: 1px solid rgba(117, 176, 255, 0.12);
        }

        .checkbox-label {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          cursor: pointer;
          padding: 0.5rem;
          border-radius: 8px;
          transition: background-color 0.2s;
        }

        .checkbox-label:hover {
          background: rgba(97, 179, 255, 0.08);
        }

        .checkbox-input {
          width: 20px;
          height: 20px;
          cursor: pointer;
          accent-color: #61b3ff;
        }

        .checkbox-text {
          font-size: 0.95rem;
          color: #d9e7ff;
        }

        .form-actions {
          display: flex;
          justify-content: center;
          margin-top: 1rem;
        }

        .btn-lg {
          padding: 1rem 3rem;
          font-size: 1.1rem;
        }

        @media (max-width: 768px) {
          .form-container {
            padding: 1.5rem;
            margin: 0 1rem;
          }

          .checkbox-group {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
      <AlertDialog
        isOpen={alertState.isOpen}
        title={alertState.title}
        message={alertState.message}
        type={alertState.type}
        onConfirm={hideAlert}
      />
    </VipLayout>
  )
}

export default VipContactPage
